import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, Monitor, Download } from 'lucide-react';
import { PLAY_STORE_URL, WEB_APP_URL } from '../config/urls';

const Demo = () => {
  // YouTube video IDs: mobile app demo (existing) and web app demo
  const mobileDemoVideoId = 'e9PG5EXPT6k';
  const webAppDemoVideoId = 'jJoXK6fEf7U';
  const embedUrl = (id) => `https://www.youtube.com/embed/${id}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4">
        <div className="container mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-full mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Play className="w-4 h-4 text-primary-500" />
              <span className="text-sm font-semibold text-primary-600">Watch Demos</span>
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              See Wellness Pro
              <span className="block text-gradient mt-2">In Action</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Watch the mobile app demo and the web application demo to explore features and capabilities in action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mobile App Demo */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-indigo-500 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 19H7V5h10v14zm0-16H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                </svg>
              </span>
              Mobile App Demo
            </h2>
            <p className="text-gray-600">See how the Wellness Pro mobile app works on your phone.</p>
          </motion.div>
          <motion.div
            className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="relative pb-[56.25%] h-0 overflow-hidden">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={embedUrl(mobileDemoVideoId)}
                title="Wellness Pro Mobile App Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Web App Demo */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-10 h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-white" />
              </span>
              Web Application Demo
            </h2>
            <p className="text-gray-600">See how the Wellness Pro web app works in your browser.</p>
          </motion.div>
          <motion.div
            className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative pb-[56.25%] h-0 overflow-hidden">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={embedUrl(webAppDemoVideoId)}
                title="Wellness Pro Web Application Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-4 pb-16 px-4">
        <div className="container mx-auto">
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <p className="text-gray-600 mb-6 text-lg">
              Ready to get started? Use the web app or download the mobile app—60 days free trial on both.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
              <a 
                href={WEB_APP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold px-6 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
              >
                <Monitor className="w-5 h-5" />
                Launch Web App
              </a>
              <a 
                href={PLAY_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight">GET IT ON</span>
                  <span className="text-lg font-bold leading-tight">Google Play</span>
                </div>
              </a>
              <Link 
                to="/features" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:border-primary-500 hover:text-primary-600 transition-all duration-300 hover:scale-105 shadow-md"
              >
                <span>Explore Features</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-full flex items-center justify-center">
                  <Play className="w-8 h-8 text-primary-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Watch & Learn</h3>
                <p className="text-gray-600">
                  See how easy it is to manage your spa business with our intuitive interface.
                </p>
              </div>
              
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-full flex items-center justify-center">
                  <Download className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">60 Days Free Trial</h3>
                <p className="text-gray-600">
                  Try all features risk-free for 60 days. No credit card required.
                </p>
              </div>
              
              <div className="p-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full flex items-center justify-center">
                  <ArrowRight className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Get Started Today</h3>
                <p className="text-gray-600">
                  Download the app and start managing your spa more efficiently right away.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Demo;

